/*
Copyright 2022 Anthony Lieuallen

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so,
subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

import React from 'react';
import { styled } from '@linaria/react';

import Paper from '@/components/Paper';
import TextLink from '@/components/Link/TextLink';

import { getColorData } from './NamedColorPicker.helpers';

function NamedColorPicker() {
  const [highlightedColor, setHighlightedColor] =
    React.useState(null);
  const [isInteracting, setIsInteracting] = React.useState(false);

  const data = React.useMemo(() => {
    return getColorData(512);
  }, []);

  return (
    <Wrapper>
      <WheelWrapper>
        <Backdrop />
        <Svg
          width="100%"
          height="100%"
          viewBox="0 0 1024 1024"
          xmlns="http://www.w3.org/2000/svg"
          onMouseEnter={() => {
            setIsInteracting(true);
          }}
          onMouseLeave={() => {
            setIsInteracting(false);
          }}
        >
          {data.map((color) => (
            <Polygon
              key={color.colorName}
              points={color.points}
              fill={color.rgb}
              stroke={color.rgb}
              strokeWidth="1px"
              onMouseEnter={() => {
                setHighlightedColor(color);
              }}
            />
          ))}
          {highlightedColor && (
            <Polygon
              key={highlightedColor.colorName}
              points={highlightedColor.points}
              fill="none"
              stroke={isInteracting ? 'black' : highlightedColor.rgb}
              strokeWidth={isInteracting ? '5px' : '1px'}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </Svg>
      </WheelWrapper>
      <ColorWrapper style={{ opacity: isInteracting ? 1 : 0 }}>
        {highlightedColor && (
          <Color>
            <ColorSquare
              style={{ backgroundColor: highlightedColor.rgb }}
            />
            <ColorName>{highlightedColor.colorName}</ColorName>
          </Color>
        )}
      </ColorWrapper>
      <Attribution>
        Created by Anthony Lieuallen. MIT Licensed.{' '}
        <TextLink href="https://github.com/arantius/web-color-wheel">
          View source on Github
        </TextLink>
        .
      </Attribution>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const WheelWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
`;

const Backdrop = styled.div`
  position: absolute;
  width: calc(100% + 8px);
  height: calc(100% + 8px);
  inset: -4px;
  background: white;
  border-radius: 50%;
  /* prettier-ignore */
  box-shadow:
    0.6px 1.3px 2.2px rgba(0, 0, 0, 0.02),
    1.5px 3.1px 5.3px rgba(0, 0, 0, 0.028),
    2.9px 5.8px 10px rgba(0, 0, 0, 0.035),
    5.1px 10.3px 17.9px rgba(0, 0, 0, 0.042);
`;

const Svg = styled.svg`
  position: relative;
  display: block;
  width: 100%;
  /*
    Turn it into a circle.
    Clip path is the best choice here because it blocks hover
    events.
  */
  clip-path: circle(50% at center);
`;

const Polygon = styled.polygon`
  transition: stroke 400ms;
`;

const ColorWrapper = styled.p`
  position: relative;
  height: 3rem;
  width: 100%;
  max-width: 275px;
  margin: 0 auto;
  transform: translateY(-12px);
  transition: opacity 250ms;
  pointer-events: none;
`;

const Color = styled(Paper)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px;
  border-radius: 8px;
  /* prettier-ignore */
  box-shadow:
    0.6px 0.7px 2.2px rgba(0, 0, 0, 0.02),
    1.5px 2px 5.3px rgba(0, 0, 0, 0.028),
    2.9px 3.5px 10px rgba(0, 0, 0, 0.035),
    5.1px 6px 17.9px rgba(0, 0, 0, 0.042),
    9.6px 12px 33.4px rgba(0, 0, 0, 0.05),
    23px 30px 80px rgba(0, 0, 0, 0.07);
`;

const ColorSquare = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 4px;
  transition: background-color 350ms;
`;

const ColorName = styled.span`
  flex: 1;
  font-size: 1.25rem;
  font-weight: var(--font-weight-medium);
`;

const Attribution = styled.p`
  font-size: 1rem;
  margin: 16px 0 48px;
`;

export default NamedColorPicker;
